import { useEffect, useCallback, useRef } from 'react';

const UC_ROOT_SELECTOR = '#usercentrics-cmp-ui';
const UC_MAIN_DIALOG_SELECTOR = '#uc-main-dialog';

interface UsercentricsUIEvent extends CustomEvent {
  detail: {
    type: string;
    source: string;
    abTestVariant?: string;
  };
}

interface UseUsercentricsStyleInjectionOptions {
  /** CSS string to inject into the shadow DOM */
  customStyle?: string;
  /** Unique ID to prevent duplicate style injection */
  styleId: string;
  /**
   * Callback executed when shadow DOM is ready.
   * Use for DOM manipulation that can't be done with CSS (e.g., SVG fill colors).
   * Called each time the banner is shown.
   */
  onReady?: (shadowRoot: ShadowRoot) => void;
}

/**
 * Hook to inject custom CSS and execute JS in Usercentrics V3 shadow DOM.
 *
 * V3 lazy loads the banner on open and destroys on close, so we need to:
 * 1. Listen for UC_UI_CMP_EVENT with CMP_SHOWN type
 * 2. Wait for shadow DOM content to be ready using MutationObserver
 * 3. Inject styles once #uc-main-dialog exists
 * 4. Execute onReady callback for JS-based DOM manipulation
 */
export const useUsercentricsStyleInjection = ({
  customStyle,
  styleId,
  onReady,
}: UseUsercentricsStyleInjectionOptions): void => {
  // Use ref to avoid recreating callbacks when onReady changes
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  const processInjection = useCallback(
    (shadowRoot: ShadowRoot): boolean => {
      // Check if the main dialog exists (indicates shadow DOM is populated)
      const mainDialog = shadowRoot.querySelector(UC_MAIN_DIALOG_SELECTOR);
      if (!mainDialog) {
        return false;
      }

      // Inject styles if provided and not already injected
      if (customStyle) {
        const existingStyle = shadowRoot.querySelector(
          `style[data-style-id="${styleId}"]`
        );
        if (!existingStyle) {
          const styleElement = document.createElement('style');
          styleElement.setAttribute('data-style-id', styleId);
          styleElement.textContent = customStyle;

          try {
            shadowRoot.appendChild(styleElement);
          } catch (e) {
            console.error(
              `[Usercentrics ${styleId}] Failed to inject styles:`,
              e
            );
          }
        }
      }

      // Execute onReady callback for JS-based manipulation
      if (onReadyRef.current) {
        try {
          onReadyRef.current(shadowRoot);
        } catch (e) {
          console.error(
            `[Usercentrics ${styleId}] Error in onReady callback:`,
            e
          );
        }
      }

      return true;
    },
    [customStyle, styleId]
  );

  const handleCmpEvent = useCallback(
    (event: UsercentricsUIEvent): void => {
      if (
        event.detail.type !== 'CMP_SHOWN' &&
        event.detail.type !== 'CMP_REQUEST_DISPLAY'
      ) {
        return;
      }

      const usercentricsRoot =
        document.querySelector<HTMLElement>(UC_ROOT_SELECTOR);

      if (!usercentricsRoot?.shadowRoot) {
        return;
      }

      const shadowRoot = usercentricsRoot.shadowRoot;

      // Try immediately first
      processInjection(shadowRoot);

      // Observe for changes
      const observer = new MutationObserver(() => {
        if (processInjection(shadowRoot)) {
          observer.disconnect();
        }
      });

      observer.observe(shadowRoot, {
        childList: true,
        subtree: true,
      });
    },
    [processInjection]
  );

  useEffect(() => {
    const eventHandler = (event: Event): void => {
      handleCmpEvent(event as UsercentricsUIEvent);
    };

    window.addEventListener('UC_UI_CMP_EVENT', eventHandler);

    return () => {
      window.removeEventListener('UC_UI_CMP_EVENT', eventHandler);
    };
  }, [handleCmpEvent]);
};
